/* T3.3 — Home reel band: inline playback, preview loop, analytics.
   Vanilla, no dependencies, deferred. Nothing here touches the network until a
   card is tapped; the clip then plays inside the card itself, not a lightbox. */
(function () {
  'use strict';

  /* ---------- analytics ----------------------------------------------------
     Every event goes three ways: dataLayer (GTM), a DOM CustomEvent (theme
     listeners), and window.reelBandTrack if the theme defines one. Wire GA4 to
     whichever you already use — do not add a second tag manager for this. */
  function track(name, payload) {
    var detail = payload || {};
    window.dataLayer = window.dataLayer || [];
    var row = { event: name };
    for (var k in detail) { if (Object.prototype.hasOwnProperty.call(detail, k)) row[k] = detail[k]; }
    window.dataLayer.push(row);
    document.dispatchEvent(new CustomEvent('reel:' + name, { detail: detail }));
    if (typeof window.reelBandTrack === 'function') window.reelBandTrack(name, detail);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
  function saveData() {
    return !!(navigator.connection && navigator.connection.saveData);
  }

  /* ---------- inline player ------------------------------------------------
     One player exists at a time. Tapping a card builds a <video> over that
     card; tapping another card (or close, Esc, or the clip ending) tears it
     down and releases the bytes. Video never loads before a tap. */
  var active = null; // { item, card, player, video }

  function closePlayer(restoreFocus) {
    if (!active) return;
    var a = active;
    active = null;
    a.video.pause();
    a.video.removeAttribute('src');
    a.video.innerHTML = '';
    a.player.remove();
    a.card.classList.remove('is-playing');
    a.card.removeAttribute('aria-hidden');
    document.removeEventListener('keydown', onKeydown);
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('reel');
      window.history.replaceState({}, '', url);
    } catch (e) { /* no-op */ }
    if (restoreFocus && a.card.focus) a.card.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') closePlayer(true);
  }

  function playCard(card, item, index, data) {
    closePlayer(false);
    if (!item) return;

    var player = document.createElement('span');
    player.className = 'reel-card__player';
    player.innerHTML =
      '<video controls playsinline preload="none" crossorigin="anonymous"></video>' +
      '<button type="button" class="reel-card__player-close" aria-label="Close video">&#10005;</button>';
    var video = player.querySelector('video');
    var close = player.querySelector('button');

    video.poster = data.poster || '';
    video.muted = true; // sound off on start; the visitor unmutes with the native control
    (data.sources || []).forEach(function (s) {
      var el = document.createElement('source');
      el.src = s.url;
      if (s.type) el.type = s.type;
      video.appendChild(el);
    });
    if (data.captions) {
      var t = document.createElement('track');
      t.kind = 'captions';
      t.label = 'English';
      t.srclang = 'en';
      t.src = data.captions;
      t.default = true;
      video.appendChild(t);
    }

    var quartiles = {};
    video.addEventListener('timeupdate', function () {
      var d = video.duration;
      if (!d || !isFinite(d)) return;
      var pct = (video.currentTime / d) * 100;
      [25, 50, 75].forEach(function (mark) {
        if (pct >= mark && !quartiles[mark]) {
          quartiles[mark] = true;
          track('reel_progress', { percent: mark, index: index, name: data.name || '' });
        }
      });
    });
    video.addEventListener('ended', function () {
      if (!quartiles[100]) {
        quartiles[100] = true;
        track('reel_progress', { percent: 100, index: index, name: data.name || '' });
      }
      closePlayer(true);
    });
    close.addEventListener('click', function () { closePlayer(true); });

    item.appendChild(player);
    card.classList.add('is-playing');
    card.setAttribute('aria-hidden', 'true');
    active = { item: item, card: card, player: player, video: video };
    document.addEventListener('keydown', onKeydown);

    video.load();
    // Captions on by default — has to be set after load, browsers reset the mode.
    video.addEventListener('loadedmetadata', function once() {
      video.removeEventListener('loadedmetadata', once);
      for (var i = 0; i < video.textTracks.length; i++) {
        if (video.textTracks[i].kind === 'captions' || video.textTracks[i].kind === 'subtitles') {
          video.textTracks[i].mode = 'showing';
        }
      }
    });
    var p = video.play();
    if (p && p.catch) p.catch(function () { /* autoplay blocked — controls are visible */ });
    close.focus();

    try {
      sessionStorage.setItem('lb_reel_opened', '1');
    } catch (e) { /* no-op */ }
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('reel', String(index + 1));
      window.history.replaceState({}, '', url);
    } catch (e) { /* no-op */ }

    track('reel_open', { index: index, name: data.name || '' });
  }

  /* ---------- preview loop (desktop, first card only) --------------------- */
  function initPreviewLoop(card) {
    var src = card.getAttribute('data-loop-src');
    if (!src) return;
    if (!window.matchMedia('(min-width: 1024px)').matches) return;
    if (prefersReducedMotion() || saveData()) return;

    var v = document.createElement('video');
    v.className = 'reel-card__loop';
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.preload = 'none';
    v.setAttribute('aria-hidden', 'true');
    v.src = src;
    card.insertBefore(v, card.querySelector('.reel-card__scrim'));

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var p = v.play();
          if (p && p.then) p.then(function () { v.setAttribute('data-playing', ''); }).catch(function () {});
        } else {
          v.pause();
          v.removeAttribute('data-playing');
        }
      });
    }, { threshold: 0.6 });
    io.observe(card);
  }

  /* ---------- rail keyboard nav ------------------------------------------- */
  function initRailKeys(band) {
    var cards = Array.prototype.slice.call(band.querySelectorAll('[data-reel-card]'));
    band.addEventListener('keydown', function (e) {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      var i = cards.indexOf(document.activeElement);
      if (i === -1) return;
      e.preventDefault();
      var next = cards[i + (e.key === 'ArrowRight' ? 1 : -1)];
      if (next) next.focus();
    });
  }

  /* ---------- boot --------------------------------------------------------- */
  function initBand(band) {
    var json = band.querySelector('[data-reel-data]');
    var data = [];
    try { data = JSON.parse(json.textContent); } catch (e) { data = []; }

    var cards = band.querySelectorAll('[data-reel-card]');
    Array.prototype.forEach.call(cards, function (card) {
      card.addEventListener('click', function () {
        var index = parseInt(card.getAttribute('data-index'), 10) || 0;
        playCard(card, card.closest('.reel-band__item'), index, data[index] || {});
      });
    });

    if (cards[0]) initPreviewLoop(cards[0]);
    initRailKeys(band);

    // reel_band_view — 50% of the section in viewport, once
    var seen = false;
    new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && !seen) {
          seen = true;
          track('reel_band_view', { cards: data.length });
        }
      });
    }, { threshold: 0.5 }).observe(band);

    // Deep link: ?reel=2 scrolls to the second card and plays it in place
    var param = new URLSearchParams(window.location.search).get('reel');
    if (param) {
      var idx = parseInt(param, 10) - 1;
      if (idx >= 0 && idx < data.length && cards[idx]) {
        cards[idx].scrollIntoView({ block: 'center', inline: 'center' });
        playCard(cards[idx], cards[idx].closest('.reel-band__item'), idx, data[idx]);
      }
    }
  }

  function boot() {
    document.querySelectorAll('[data-reel-band]').forEach(initBand);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Shopify theme editor: sections re-render on edit
  document.addEventListener('shopify:section:load', function (e) {
    var band = e.target.querySelector('[data-reel-band]');
    if (band) initBand(band);
  });

  /* reel_band_to_product — did a session that opened a reel go on to the product?
     Fires on product links and add-to-cart submits when the session flag is set. */
  document.addEventListener('click', function (e) {
    var hit = e.target.closest && e.target.closest('a[href*="/products/"], button[name="add"], [data-reel-cta]');
    if (!hit) return;
    var opened = false;
    try { opened = sessionStorage.getItem('lb_reel_opened') === '1'; } catch (err) {}
    if (opened) track('reel_band_to_product', { href: hit.getAttribute('href') || 'add_to_cart' });
  });
})();
